import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ExpandMore as ExpandMoreIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { formatAppDateTime, appDateValue } from '../../utils/dateFormat';
import {
  cabinetContractApi,
  cabinetCreativeApi,
  cabinetDictApi,
  cabinetMediaApi,
  cabinetPlatformApi,
  cabinetSelfAdChannelApi,
  DictItem,
  MonitorChannel,
  schedulerApi,
  settingsApi,
} from '../../services/api';
import { ContractD, CreativeD, CreativeType, MediaUpload, PlatformD } from '../../types';
import PageHeader from '../../components/PageHeader';
import MobileEntityCard, { ActionBtn } from '../../components/MobileEntityCard';
import { useAuth } from '../../contexts/AuthContext';

const statusLabel: Record<string, string> = {
  DRAFT: 'Черновик',
  SENT_TO_ORD: 'В ОРД',
  AWAITING_LINKS: 'Ждёт ссылок',
  LINKED: 'Ссылки',
  REPORTING: 'В отчётности',
  SKIP_STATS: 'Не сдаём',
  MISSING_LINKS: 'Ссылка не найдена',
  PUBLICATION_ENDED: 'Срок истёк',
};

const CreativesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isOwner, isClientAdmin, user } = useAuth();
  const canSelfAd = isOwner || !!user?.allowSelfAd;
  const clientTestMode = !!user?.testModeEnabled;
  const statsSubmissionEnabled = user?.statsSubmissionEnabled !== false;
  const dzenEnabled = !!user?.dzenSubmissionEnabled;
  const showSelfAdChannels = !!user?.ordClientId && (canSelfAd || isClientAdmin);
  const [items, setItems] = useState<CreativeD[]>([]);
  const [contracts, setContracts] = useState<ContractD[]>([]);
  const [forms, setForms] = useState<DictItem[]>([]);
  const [payTypes, setPayTypes] = useState<DictItem[]>([]);
  const [kktuOptions, setKktuOptions] = useState<DictItem[]>([]);
  const [kktuSearch, setKktuSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [enrichingDates, setEnrichingDates] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [texts, setTexts] = useState('');
  const [form, setForm] = useState('video');
  const [payType, setPayType] = useState('other');
  const [kktu, setKktu] = useState<DictItem | null>({ code: '30.15.1', name: 'Прочие товары и услуги' });
  const [mediaExternalIds, setMediaExternalIds] = useState('');
  const [uploadedMedia, setUploadedMedia] = useState<MediaUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [creativeType, setCreativeType] = useState<CreativeType>('SELF_AD');
  const [contractDId, setContractDId] = useState<number | ''>('');
  const [priceAmount, setPriceAmount] = useState('');
  const [publicationEndDate, setPublicationEndDate] = useState('');
  const [linksOpen, setLinksOpen] = useState(false);
  const [linksCreative, setLinksCreative] = useState<CreativeD | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [vkUrl, setVkUrl] = useState('');
  const [rutubeUrl, setRutubeUrl] = useState('');
  const [dzenUrl, setDzenUrl] = useState('');
  const [telegramUrl, setTelegramUrl] = useState('');
  const [platforms, setPlatforms] = useState<PlatformD[]>([]);
  const [youtubePlatformDId, setYoutubePlatformDId] = useState<number | ''>('');
  const [vkPlatformDId, setVkPlatformDId] = useState<number | ''>('');
  const [rutubePlatformDId, setRutubePlatformDId] = useState<number | ''>('');
  const [dzenPlatformDId, setDzenPlatformDId] = useState<number | ''>('');
  const [telegramPlatformDId, setTelegramPlatformDId] = useState<number | ''>('');
  const [reporting, setReporting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [eridSearch, setEridSearch] = useState('');
  const [testMode, setTestMode] = useState(false);
  const manualTestMode = isOwner ? testMode : clientTestMode;
  const [manualActionId, setManualActionId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const [selfChannels, setSelfChannels] = useState<MonitorChannel[]>([]);
  const [selfChannelsLoading, setSelfChannelsLoading] = useState(false);
  const [selfChannelSaving, setSelfChannelSaving] = useState(false);
  const [selfChannelSyncing, setSelfChannelSyncing] = useState(false);
  const [selfChannelPlatform, setSelfChannelPlatform] = useState<'YOUTUBE' | 'VK_VIDEO' | 'RUTUBE' | 'TELEGRAM'>('YOUTUBE');
  const [selfChannelInput, setSelfChannelInput] = useState('');

  useEffect(() => {
    if (!canSelfAd && creativeType === 'SELF_AD') {
      setCreativeType('THIRD_PARTY');
    }
  }, [canSelfAd, creativeType]);

  const load = async (opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) {
        setLoading(true);
      }
      setError(null);
      const [creatives, contractsList, formsList, payTypesList, testModeRes] = await Promise.all([
        cabinetCreativeApi.list(),
        // У менеджера/монтажёра часто нет права на договоры — 403 не должен валить весь экран
        cabinetContractApi.list().catch(() => [] as ContractD[]),
        cabinetDictApi.forms().catch(() => [] as DictItem[]),
        cabinetDictApi.payTypes().catch(() => [] as DictItem[]),
        isOwner
          ? settingsApi.getTestMode().catch(() => ({ enabled: false }))
          : Promise.resolve({ enabled: false }),
      ]);
      setItems(creatives);
      setContracts(contractsList);
      setForms(formsList);
      setPayTypes(payTypesList);
      setTestMode(isOwner ? !!testModeRes.enabled : !!user?.testModeEnabled);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  };

  const mergeCreative = (updated: CreativeD) => {
    setItems((prev) => {
      const idx = prev.findIndex((c) => c.creativeDId === updated.creativeDId);
      if (idx < 0) return [...prev, updated];
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
  };

  const focusCreativeInList = (c: CreativeD) => {
    if (c.erid) {
      setEridSearch(c.erid);
    } else if (c.name) {
      setEridSearch(c.name);
    }
    setHighlightId(c.creativeDId);
    setTimeout(() => setHighlightId(null), 4000);
    requestAnimationFrame(() => {
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const creativeSortTs = (c: CreativeD) =>
    appDateValue(c.ordCreatedTs || c.changedTs || c.createdTs);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!showSelfAdChannels) return;
    (async () => {
      try {
        setSelfChannelsLoading(true);
        setSelfChannels(await cabinetSelfAdChannelApi.list());
      } catch {
        setSelfChannels([]);
      } finally {
        setSelfChannelsLoading(false);
      }
    })();
  }, [showSelfAdChannels]);

  const addSelfChannel = async () => {
    if (!selfChannelInput.trim()) return;
    try {
      setSelfChannelSaving(true);
      setError(null);
      await cabinetSelfAdChannelApi.add({
        platform: selfChannelPlatform,
        channelId: selfChannelInput.trim(),
      });
      setSelfChannelInput('');
      setSelfChannels(await cabinetSelfAdChannelApi.list());
      setSuccess('Канал саморекламы добавлен');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось добавить канал');
    } finally {
      setSelfChannelSaving(false);
    }
  };

  const removeSelfChannel = async (id: number) => {
    try {
      await cabinetSelfAdChannelApi.remove(id);
      setSelfChannels(await cabinetSelfAdChannelApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось удалить канал');
    }
  };

  const syncSelfChannels = async () => {
    try {
      setSelfChannelSyncing(true);
      setError(null);
      const res = await cabinetSelfAdChannelApi.sync();
      setSuccess(
        `Собрано каналов: ${res.channelsProcessed}, привязано креативов: ${res.matched}`
        + ` (YouTube: ${res.linkedYoutube}, VK: ${res.linkedVk}, Rutube: ${res.linkedRutube ?? 0}, Telegram: ${res.linkedTelegram ?? 0}).`
        + ` Ещё ждут: ${res.stillAwaiting}`
      );
      await load({ silent: true });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сбора каналов саморекламы');
    } finally {
      setSelfChannelSyncing(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      cabinetDictApi.kktu(kktuSearch || undefined)
        .then(setKktuOptions)
        .catch(() => setKktuOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [kktuSearch]);

  const resetFormFields = () => {
    setName('');
    setBrand('');
    setCategory('');
    setDescription('');
    setTexts('');
    setForm('video');
    setPayType('other');
    setKktu({ code: '30.15.1', name: 'Прочие товары и услуги' });
    setMediaExternalIds('');
    setUploadedMedia([]);
    setUploadProgress(0);
    setUploadCurrent(0);
    setUploadTotal(0);
    setTargetUrl('');
    setPriceAmount('');
    setPublicationEndDate('');
    setCreativeType('SELF_AD');
    setContractDId('');
    setEditingId(null);
  };

  const cancelForm = () => {
    resetFormFields();
    setFormOpen(false);
    setSuccess('Создание/редактирование отменено');
    setError(null);
  };

  const openFormSheet = () => {
    setFormOpen(true);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const fillFromCreative = (c: CreativeD, asCopy: boolean) => {
    setName(asCopy ? (c.name.endsWith(' (копия)') ? c.name : `${c.name} (копия)`) : c.name);
    setBrand(c.brand || '');
    setCategory(c.category || '');
    setDescription(c.description || '');
    setTexts(c.texts || '');
    setForm(c.form || 'video');
    setPayType(c.payType || 'other');
    setKktu(c.kktu ? { code: c.kktu, name: c.kktu } : { code: '30.15.1', name: 'Прочие товары и услуги' });
    setCreativeType(c.creativeType || 'SELF_AD');
    setContractDId(c.contractDId || '');
    setTargetUrl(c.targetUrl || '');
    setPriceAmount(c.priceAmount != null ? String(c.priceAmount) : '');
    setPublicationEndDate(c.publicationEndDate ? String(c.publicationEndDate).slice(0, 10) : '');
    if (asCopy) {
      setEditingId(null);
      setMediaExternalIds('');
      setUploadedMedia([]);
      setYoutubeUrl('');
      setVkUrl('');
      setSuccess('Форма заполнена из креатива (без медиа и ссылок), цена скопирована');
    } else {
      setEditingId(c.creativeDId);
      setMediaExternalIds(c.mediaExternalIds || '');
      setUploadedMedia([]);
      setSuccess(`Редактирование: ${c.name}`);
    }
    openFormSheet();
  };

  const saveCreative = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      if (!form.trim()) {
        setError('Укажите форму распространения креатива');
        return;
      }
      if ((kktu?.code || '30.15.1') === '30.15.1'
          && !brand.trim() && !category.trim() && !description.trim()) {
        setError('Для ККТУ «Прочие товары и услуги» укажите бренд, категорию или описание');
        return;
      }
      const payload = {
        name,
        brand,
        category,
        description,
        texts,
        mediaExternalIds,
        targetUrl,
        form,
        payType,
        kktu: kktu?.code || '30.15.1',
        creativeType,
        personExternalId: 'my',
        contractDId: creativeType === 'THIRD_PARTY' ? contractDId : null,
        priceAmount: creativeType === 'THIRD_PARTY'
          ? (priceAmount.trim() === '' ? null : Number(priceAmount))
          : null,
        publicationEndDate: publicationEndDate.trim() || null,
        clearPublicationEndDate: editingId != null && !publicationEndDate.trim(),
        syncToOrd: editingId == null,
      };
      if (creativeType === 'THIRD_PARTY' && (payload.priceAmount == null || Number.isNaN(payload.priceAmount) || payload.priceAmount <= 0)) {
        setError('Укажите цену интеграции больше 0');
        return;
      }
      if (editingId != null) {
        await cabinetCreativeApi.update(editingId, payload);
        setSuccess('Креатив обновлён');
      } else {
        const created = await cabinetCreativeApi.create(payload);
        setSuccess(`Креатив создан. ERID: ${created.erid || '—'}`);
      }
      resetFormFields();
      setFormOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const deleteCreative = async (c: CreativeD) => {
    if (!window.confirm(`Удалить креатив «${c.name}» из кабинета? (в ОРД останется)`)) {
      return;
    }
    try {
      setError(null);
      await cabinetCreativeApi.delete(c.creativeDId);
      if (editingId === c.creativeDId) {
        resetFormFields();
        setFormOpen(false);
      }
      setSuccess('Креатив удалён из кабинета');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка удаления');
    }
  };

  const onPickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    const maxBytes = 260 * 1024 * 1024;
    const tooBig = files.find((f) => f.size > maxBytes);
    if (tooBig) {
      setError(`Файл слишком большой: максимум 260 Мб (лимит ОРД VK). «${tooBig.name}»: ${(tooBig.size / (1024 * 1024)).toFixed(1)} Мб`);
      return;
    }
    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadCurrent(0);
      setUploadTotal(files.length);
      setError(null);
      setSuccess(null);
      const uploadedBatch: MediaUpload[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadCurrent(i + 1);
        setUploadProgress(0);
        const uploaded = await cabinetMediaApi.upload(file, setUploadProgress);
        uploadedBatch.push(uploaded);
      }
      setUploadedMedia((prev) => [...prev, ...uploadedBatch]);
      const newIds = uploadedBatch.map((m) => m.mediaExternalId).join(',');
      setMediaExternalIds((prev) => (prev.trim() ? `${prev.trim()},${newIds}` : newIds));
      setSuccess(
        uploadedBatch.length === 1
          ? `Файл загружен в ОРД: ${uploadedBatch[0].filename}`
          : `Загружено в ОРД: ${uploadedBatch.length} файлов`
      );
      if (!name.trim() && uploadedBatch[0]) {
        setName(uploadedBatch[0].filename.replace(/\.[^.]+$/, ''));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки файла в ОРД');
    } finally {
      setUploading(false);
      setUploadCurrent(0);
      setUploadTotal(0);
    }
  };

  const removeUploadedMedia = (mediaExternalId: string) => {
    setUploadedMedia((prev) => prev.filter((m) => m.mediaExternalId !== mediaExternalId));
    setMediaExternalIds((prev) =>
      prev
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter((id) => id && id !== mediaExternalId)
        .join(',')
    );
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const platformsByHosting = useMemo(() => {
    const map: Record<string, PlatformD[]> = {
      YOUTUBE: [],
      VK_VIDEO: [],
      RUTUBE: [],
      DZEN: [],
      TELEGRAM: [],
    };
    for (const p of platforms) {
      const key = p.hostingCd || '';
      if (map[key]) map[key].push(p);
    }
    return map;
  }, [platforms]);

  const openLinks = async (c: CreativeD) => {
    const fillFrom = (item: CreativeD) => {
      setLinksCreative(item);
      setYoutubeUrl(item.youtubeUrl || (item.youtubeVideoId ? `https://www.youtube.com/watch?v=${item.youtubeVideoId}` : ''));
      setVkUrl(item.vkUrl || (item.vkVideoId ? `https://vkvideo.ru/video${item.vkVideoId}` : ''));
      setRutubeUrl(item.rutubeUrl || (item.rutubeVideoId ? `https://rutube.ru/video/${item.rutubeVideoId}/` : ''));
      setDzenUrl(item.dzenUrl || (item.dzenVideoId ? `https://dzen.ru/video/watch/${item.dzenVideoId}` : ''));
      setTelegramUrl(item.telegramUrl || (item.telegramVideoId ? `https://t.me/${item.telegramVideoId}` : ''));
    };
    setYoutubePlatformDId('');
    setVkPlatformDId('');
    setRutubePlatformDId('');
    setDzenPlatformDId('');
    setTelegramPlatformDId('');
    fillFrom(c);
    setLinksOpen(true);
    try {
      const [fresh, pads] = await Promise.all([
        cabinetCreativeApi.get(c.creativeDId),
        cabinetPlatformApi.list().catch(() => [] as PlatformD[]),
      ]);
      fillFrom(fresh);
      mergeCreative(fresh);
      setPlatforms(pads);
    } catch {
      // оставляем значения из списка
    }
  };

  const saveLinks = async () => {
    if (!linksCreative) return;
    try {
      setSaving(true);
      setError(null);
      const updated = await cabinetCreativeApi.attachLinks(linksCreative.creativeDId, {
        youtubeUrl,
        vkUrl,
        rutubeUrl,
        telegramUrl,
        ...(dzenEnabled ? { dzenUrl } : {}),
        ...(youtubePlatformDId ? { youtubePlatformDId: Number(youtubePlatformDId) } : {}),
        ...(vkPlatformDId ? { vkPlatformDId: Number(vkPlatformDId) } : {}),
        ...(rutubePlatformDId ? { rutubePlatformDId: Number(rutubePlatformDId) } : {}),
        ...(telegramPlatformDId ? { telegramPlatformDId: Number(telegramPlatformDId) } : {}),
        ...(dzenEnabled && dzenPlatformDId ? { dzenPlatformDId: Number(dzenPlatformDId) } : {}),
      });
      mergeCreative(updated);
      setSuccess(`«${updated.name}» в отчётности — показан в списке ниже`);
      setLinksOpen(false);
      setLinksCreative(null);
      focusCreativeInList(updated);
      await load({ silent: true });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка ссылок');
    } finally {
      setSaving(false);
    }
  };

  const runMonthly = async () => {
    try {
      setReporting(true);
      setError(null);
      await schedulerApi.startCabinetMonthlyReport();
      setSuccess('Запущен полный цикл: сбор видео → сопоставление ссылок → просмотры → сдача (акты/статистика)');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка запуска сдачи');
    } finally {
      setReporting(false);
    }
  };

  const runManualAction = async (c: CreativeD, action: 'collect' | 'invoice' | 'stats') => {
    const key = `${action}-${c.creativeDId}`;
    const labels = {
      collect: 'Собрать просмотры',
      invoice: 'Сдать акт',
      stats: 'Сдать статистику',
    };
    if (!window.confirm(`${labels[action]} для «${c.name}» (${c.erid || 'без erid'})?`)) return;
    try {
      setManualActionId(key);
      setError(null);
      if (action === 'collect') {
        const count = await cabinetCreativeApi.collectViews(c.creativeDId);
        setSuccess(`Просмотры собраны (${count} видео): ${c.name}`);
      } else if (action === 'invoice') {
        await cabinetCreativeApi.submitInvoice(c.creativeDId);
        setSuccess(`Акт сдан: ${c.name}`);
      } else {
        await cabinetCreativeApi.submitStats(c.creativeDId);
        setSuccess(`Статистика сдана: ${c.name}`);
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || `Ошибка: ${labels[action]}`);
    } finally {
      setManualActionId(null);
    }
  };

  const importFromOrd = async () => {
    try {
      setImporting(true);
      setError(null);
      const result = await cabinetCreativeApi.importFromOrd();
      const errHint = result.errors?.length
        ? ` Ошибки (${result.errors.length}): ${result.errors.slice(0, 3).join('; ')}`
        : '';
      setSuccess(
        `Из ОРД: контрагенты +${result.personsImported}/~${result.personsUpdated}, ` +
        `договоры +${result.contractsImported}/~${result.contractsUpdated}, ` +
        `креативы +${result.creativesImported}/~${result.creativesUpdated}.` +
        errHint
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка импорта из ОРД');
    } finally {
      setImporting(false);
    }
  };

  const enrichDates = async () => {
    try {
      setEnrichingDates(true);
      setError(null);
      const n = await cabinetCreativeApi.enrichOrdDates();
      setSuccess(`Даты создания из ОРД обновлены: ${n}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка обновления дат');
    } finally {
      setEnrichingDates(false);
    }
  };

  const autoLinkFromCollected = async () => {
    try {
      setLinking(true);
      setError(null);
      const r = await cabinetCreativeApi.autoLinkFromCollected();
      setSuccess(
        `Каналов собрано: ${r.channelsProcessed ?? 0}. ` +
        `Сопоставлено: ${r.matched} из ${r.scanned}. ` +
        `YT: ${r.linkedYoutube}, VK: ${r.linkedVk}, Rutube: ${r.linkedRutube ?? 0}, Telegram: ${r.linkedTelegram ?? 0}. Ещё ждут ссылок: ${r.stillAwaiting}.` +
        (r.datesEnriched != null ? ` Дат ОРД дозаполнено: ${r.datesEnriched}.` : '')
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сопоставления ссылок');
    } finally {
      setLinking(false);
    }
  };

  const copyCreative = async (c: CreativeD) => {
    try {
      setError(null);
      // только форма — без черновика в БД, чтобы не занимать бюджет «пустой» копией
      fillFromCreative(
        {
          ...c,
          name: c.name.endsWith(' (копия)') ? c.name : `${c.name} (копия)`,
        },
        true
      );
      setEditingId(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || null);
    }
  };

  const awaiting = useMemo(
    () => items.filter((c) => c.status === 'AWAITING_LINKS' || c.status === 'SENT_TO_ORD'),
    [items]
  );

  const missingLinks = useMemo(
    () => items.filter((c) => c.status === 'MISSING_LINKS'),
    [items]
  );

  const contractBudget = useMemo(() => {
    if (creativeType !== 'THIRD_PARTY' || !contractDId) return null;
    const contract = contracts.find((c) => c.contractDId === contractDId);
    if (contract?.amount == null) return null;
    const amount = Number(contract.amount);
    const used = items
      .filter((c) => c.creativeType === 'THIRD_PARTY' && c.contractDId === contractDId)
      .filter((c) => editingId == null || c.creativeDId !== editingId)
      .filter((c) => c.syncedToOrd || !!c.erid)
      .reduce((sum, c) => sum + (c.priceAmount != null ? Number(c.priceAmount) : 0), 0);
    const price = priceAmount.trim() === '' ? 0 : Number(priceAmount);
    const remaining = amount - used;
    return {
      amount,
      used,
      remaining,
      afterSave: remaining - (Number.isNaN(price) ? 0 : price),
    };
  }, [creativeType, contractDId, contracts, items, editingId, priceAmount]);

  const canSkipCreative = (c: CreativeD) =>
    c.canEdit !== false
    && (c.status === 'AWAITING_LINKS' || c.status === 'SENT_TO_ORD' || c.status === 'REPORTING' || c.status === 'MISSING_LINKS');

  const acceptMissing = async (c: CreativeD) => {
    try {
      setError(null);
      await cabinetCreativeApi.skipStats(c.creativeDId);
      setSuccess(`Принято (не сдаём): ${c.name}`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось принять');
    }
  };

  const acceptAllMissing = async () => {
    if (!missingLinks.length) return;
    if (!window.confirm(`Принять все (${missingLinks.length}) — статистика по ним больше не сдаётся?`)) return;
    try {
      setError(null);
      const n = await cabinetCreativeApi.acceptAllMissingLinks();
      setSuccess(`Принято: ${n}`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось принять все');
    }
  };

  const fixMissing = async (c: CreativeD) => {
    await openLinks(c);
  };
  const skipStats = async (c: CreativeD) => {
    const ok = window.confirm(
      `Пропустить сдачу статистики по «${c.name}» (${c.erid || 'без erid'})?\n\n`
      + 'Подходит для ТГ и других площадок без авто-сбора. Креатив останется в списке со статусом «Не сдаём».'
    );
    if (!ok) return;
    try {
      setError(null);
      await cabinetCreativeApi.skipStats(c.creativeDId);
      setSuccess(`Исключён из сдачи: ${c.name}`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось исключить');
    }
  };

  const restoreStats = async (c: CreativeD) => {
    const ok = window.confirm(
      `Вернуть «${c.name}» (${c.erid || 'без erid'}) в сдачу / «ждут ссылок»?`
    );
    if (!ok) return;
    try {
      setError(null);
      await cabinetCreativeApi.restoreStats(c.creativeDId);
      setSuccess(`Возвращён: ${c.name}`);
      setTimeout(() => setSuccess(null), 3000);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось вернуть');
    }
  };

  const sortedItems = useMemo(() => {
    const q = eridSearch.trim().toLowerCase();
    const filtered = !q
      ? items
      : items.filter((c) => {
          const erid = (c.erid || '').toLowerCase();
          const name = (c.name || '').toLowerCase();
          return erid.includes(q) || name.includes(q);
        });
    return [...filtered].sort((a, b) => {
      const db = creativeSortTs(b);
      const da = creativeSortTs(a);
      if (db !== da) return db - da;
      return (b.creativeDId || 0) - (a.creativeDId || 0);
    });
  }, [items, eridSearch]);

  const canCreate =
    !!name.trim()
    && !!mediaExternalIds.trim()
    && !!kktu?.code
    && !!form
    && !!payType
    && (creativeType !== 'THIRD_PARTY' || (!!contractDId && !!priceAmount));

  const renderCreativeActions = (c: CreativeD) => (
    <>
      {c.canEdit !== false && (
        <ActionBtn onClick={() => fillFromCreative(c, false)}>Изменить</ActionBtn>
      )}
      <ActionBtn onClick={() => copyCreative(c)}>Копировать</ActionBtn>
      {c.canEdit !== false && (
        <ActionBtn onClick={() => openLinks(c)}>Ссылки</ActionBtn>
      )}
      {manualTestMode && c.status === 'REPORTING' && c.canEdit !== false && (
        <>
          <ActionBtn
            disabled={manualActionId === `collect-${c.creativeDId}`}
            onClick={() => runManualAction(c, 'collect')}
          >
            {manualActionId === `collect-${c.creativeDId}` ? '…' : 'Собрать просмотры'}
          </ActionBtn>
          {c.creativeType === 'THIRD_PARTY' && !c.firstInvoiceDone && (
            <ActionBtn
              disabled={manualActionId === `invoice-${c.creativeDId}`}
              onClick={() => runManualAction(c, 'invoice')}
            >
              {manualActionId === `invoice-${c.creativeDId}` ? '…' : 'Сдать акт'}
            </ActionBtn>
          )}
          {statsSubmissionEnabled && (
            <ActionBtn
              disabled={manualActionId === `stats-${c.creativeDId}`}
              onClick={() => runManualAction(c, 'stats')}
            >
              {manualActionId === `stats-${c.creativeDId}` ? '…' : 'Сдать статистику'}
            </ActionBtn>
          )}
        </>
      )}
      {canSkipCreative(c) && c.status !== 'MISSING_LINKS' && (
        <ActionBtn onClick={() => skipStats(c)}>Пропустить</ActionBtn>
      )}
      {c.status === 'MISSING_LINKS' && c.canEdit !== false && (
        <>
          <ActionBtn onClick={() => fixMissing(c)}>Исправить</ActionBtn>
          <ActionBtn onClick={() => acceptMissing(c)}>Ок</ActionBtn>
        </>
      )}
      {(c.status === 'SKIP_STATS' || c.status === 'PUBLICATION_ENDED') && c.canEdit !== false && (
        <ActionBtn onClick={() => restoreStats(c)}>Вернуть в сдачу</ActionBtn>
      )}
      {!c.syncedToOrd && c.canEdit !== false && (
        <ActionBtn onClick={() => cabinetCreativeApi.sendToOrd(c.creativeDId).then(() => load())}>В ОРД</ActionBtn>
      )}
      {c.canDelete !== false && (
        <ActionBtn color="error" onClick={() => deleteCreative(c)}>Удалить</ActionBtn>
      )}
    </>
  );

  return (
    <Box>
      <PageHeader
        title="Креативы"
        description={
          isOwner
            ? "Поля со * обязательны. «Из ОРД» импортирует контрагентов, договоры и креативы."
            : isClientAdmin
              ? "Создавайте креативы по своим договорам и отправляйте их в ОРД."
              : "Создавайте креативы по своим договорам. Опасные операции с ОРД доступны только владельцу."
        }
        actions={
          isOwner ? (
            <>
              <Button variant="outlined" disabled={importing} onClick={importFromOrd}>
                {importing ? <CircularProgress size={18} /> : 'Из ОРД'}
              </Button>
              <Button variant="outlined" disabled={enrichingDates} onClick={enrichDates}>
                {enrichingDates ? <CircularProgress size={18} /> : 'Даты ОРД'}
              </Button>
              <Button variant="contained" disabled={linking} onClick={autoLinkFromCollected}>
                {linking ? <CircularProgress size={18} /> : 'Сопоставить ссылки'}
              </Button>
              <Button variant="outlined" disabled={reporting || testMode} onClick={runMonthly}>
                {reporting ? <CircularProgress size={18} /> : 'Сдать месяц'}
              </Button>
            </>
          ) : undefined
        }
      />
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}
      {manualTestMode && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Тестовый режим включён — на креативах в отчётности доступны ручные кнопки сдачи.
          {isClientAdmin ? ' Выключить можно в настройках кабинета.' : ''}
        </Alert>
      )}

      {showSelfAdChannels && (
        <Accordion disableGutters sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">Каналы саморекламы</Typography>
              {!selfChannelsLoading && selfChannels.length > 0 && (
                <Chip size="small" label={selfChannels.length} />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Добавьте свои YouTube / VK / Rutube каналы — система соберёт ролики и привяжет
              ссылки к креативам саморекламы по erid в описании. Для контрагентов каналы
              по-прежнему настраиваются в разделе «Контрагенты».
            </Typography>
            {selfChannelsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Stack spacing={1.5}>
                {selfChannels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Каналы не добавлены</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Платформа</TableCell>
                        <TableCell>Название</TableCell>
                        <TableCell>ID / ссылка</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selfChannels.map((ch) => (
                        <TableRow key={ch.id}>
                          <TableCell>
                            {ch.platform === 'YOUTUBE' ? 'YouTube'
                              : ch.platform === 'RUTUBE' ? 'Rutube'
                              : ch.platform === 'TELEGRAM' ? 'Telegram'
                              : 'VK'}
                          </TableCell>
                          <TableCell>{ch.name}</TableCell>
                          <TableCell sx={{ wordBreak: 'break-all' }}>{ch.channelId}</TableCell>
                          <TableCell>
                            <Button size="small" color="error" onClick={() => removeSelfChannel(ch.id)}>
                              Удалить
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <FormControl sx={{ minWidth: 140 }} fullWidth>
                    <InputLabel>Платформа</InputLabel>
                    <Select
                      label="Платформа"
                      value={selfChannelPlatform}
                      onChange={(e) => setSelfChannelPlatform(e.target.value as 'YOUTUBE' | 'VK_VIDEO' | 'RUTUBE' | 'TELEGRAM')}
                    >
                      <MenuItem value="YOUTUBE">YouTube</MenuItem>
                      <MenuItem value="VK_VIDEO">VK Video</MenuItem>
                      <MenuItem value="RUTUBE">Rutube</MenuItem>
                      <MenuItem value="TELEGRAM">Telegram</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Ссылка или ID канала"
                    placeholder={
                      selfChannelPlatform === 'TELEGRAM'
                        ? 'https://t.me/channel или @channel (только публичные)'
                        : selfChannelPlatform === 'RUTUBE'
                          ? 'https://rutube.ru/channel/…'
                          : 'Ссылка или ID канала'
                    }
                    value={selfChannelInput}
                    onChange={(e) => setSelfChannelInput(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    disabled={selfChannelSaving || !selfChannelInput.trim()}
                    onClick={addSelfChannel}
                    sx={{ flexShrink: 0 }}
                  >
                    {selfChannelSaving ? <CircularProgress size={20} /> : 'Добавить'}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={selfChannelSyncing || selfChannels.length === 0}
                    onClick={syncSelfChannels}
                    sx={{ flexShrink: 0 }}
                  >
                    {selfChannelSyncing ? <CircularProgress size={20} /> : 'Собрать и привязать'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {missingLinks.length > 0 && (
        <Accordion defaultExpanded disableGutters sx={{ mb: 2, bgcolor: 'rgba(239, 68, 68, 0.08)' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Эти креативы по ссылкам/каналам не найдены, статистика не сдаётся ({missingLinks.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button size="small" variant="contained" color="warning" onClick={acceptAllMissing}>
                Принять всё
              </Button>
            </Stack>
            <Stack spacing={1} sx={{ maxHeight: 360, overflow: 'auto' }}>
              {missingLinks.map((c) => (
                <Stack
                  key={c.creativeDId}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={600} noWrap={!isMobile}>{c.name}</Typography>
                    <Typography variant="caption" color="error" display="block">
                      {c.lastError || 'Ролик не найден'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={c.erid || 'нет erid'} />
                  {c.canEdit !== false && (
                    <>
                      <Button size="small" variant="contained" onClick={() => fixMissing(c)}>Исправить</Button>
                      <Button size="small" variant="outlined" onClick={() => acceptMissing(c)}>Ок</Button>
                    </>
                  )}
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {awaiting.length > 0 && (
        <Accordion disableGutters sx={{ mb: 2, bgcolor: 'rgba(245, 158, 11, 0.08)' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Ждут ссылок ({awaiting.length})</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {isOwner
                ? 'Кнопка сначала заново соберёт ролики со всех включённых каналов (со сбросом кэша), затем сопоставит по erid в описании. В описании видео должен быть текст вида «erid: …».'
                : 'Добавьте ссылки на свои креативы. Массовое сопоставление делает только владелец.'}
            </Typography>
            <Stack spacing={1} sx={{ maxHeight: 320, overflow: 'auto' }}>
              {awaiting.map((c) => (
                <Stack
                  key={c.creativeDId}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography flex={1} noWrap={!isMobile} sx={{ fontWeight: 600 }}>{c.name}</Typography>
                  <Chip size="small" label={c.erid || 'нет erid'} />
                  {c.canEdit !== false && (
                    <Button size="small" variant="contained" onClick={() => openLinks(c)}>Добавить ссылки</Button>
                  )}
                  {canSkipCreative(c) && (
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      sx={{ fontSize: 11, minWidth: 0, px: 0.75, opacity: 0.65 }}
                      onClick={() => skipStats(c)}
                    >
                      Пропустить
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion
        disableGutters
        expanded={formOpen}
        onChange={(_, open) => setFormOpen(open)}
        ref={formRef}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1">
              {editingId != null ? `Редактирование #${editingId}` : 'Новый креатив'}
            </Typography>
            {!formOpen && (
              <Typography variant="caption" color="text.secondary">
                обычно копируют из списка ниже
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField required label="Название *" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
            <FormControl required fullWidth sx={{ minWidth: 0, maxWidth: { md: 200 } }}>
              <InputLabel>Тип *</InputLabel>
              <Select label="Тип *" value={creativeType} onChange={(e) => setCreativeType(e.target.value as CreativeType)}>
                {canSelfAd && <MenuItem value="SELF_AD">Самореклама</MenuItem>}
                <MenuItem value="THIRD_PARTY">Сторонняя</MenuItem>
              </Select>
            </FormControl>
            {creativeType === 'THIRD_PARTY' && (
              <>
                <FormControl required fullWidth sx={{ flex: { md: '1 1 220px' }, minWidth: 0, maxWidth: { md: 280 } }}>
                  <InputLabel>Договор *</InputLabel>
                  <Select
                    label="Договор *"
                    value={contractDId}
                    onChange={(e) => setContractDId(e.target.value as number)}
                  >
                    {contracts.map((c) => (
                      <MenuItem key={c.contractDId} value={c.contractDId}>
                        #{c.contractDId} {c.counterpartyName}
                        {c.amount != null ? ` · ${c.amount}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  required
                  label="Цена интеграции *"
                  value={priceAmount}
                  onChange={(e) => setPriceAmount(e.target.value)}
                  fullWidth
                  sx={{ flex: { md: '2 1 280px' }, minWidth: 0 }}
                  helperText={
                    contractBudget
                      ? `Договор ${contractBudget.amount}: занято ${contractBudget.used}, остаток ${contractBudget.remaining}`
                        + (priceAmount.trim()
                          ? ` → после сохранения ${contractBudget.afterSave}`
                          : '')
                      : contractDId
                        ? 'У договора не указана сумма — лимит не проверяется'
                        : undefined
                  }
                  error={!!contractBudget && contractBudget.afterSave < 0}
                />
              </>
            )}
          </Stack>

          <TextField
            label="Срок публикации до"
            type="date"
            value={publicationEndDate}
            onChange={(e) => setPublicationEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            helperText="Пусто = бессрочно. В день окончания в 00:05 МСК — финальный сбор просмотров; статистика за этот месяц сдаётся 1-го следующего, дальше не сдаём."
            sx={{ maxWidth: { sm: 420 } }}
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <FormControl required fullWidth sx={{ minWidth: 0 }}>
              <InputLabel>Форма распространения *</InputLabel>
              <Select
                label="Форма распространения *"
                value={form}
                onChange={(e) => setForm(e.target.value)}
              >
                {forms.map((f) => (
                  <MenuItem key={f.code} value={f.code}>{f.name}</MenuItem>
                ))}
                {/* если из ОРД пришла форма, которой ещё нет в списке — показываем как есть */}
                {form && !forms.some((f) => f.code === form) && (
                  <MenuItem value={form}>{form}</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl required fullWidth sx={{ minWidth: 0, maxWidth: { md: 200 } }}>
              <InputLabel>Тип оплаты *</InputLabel>
              <Select label="Тип оплаты *" value={payType} onChange={(e) => setPayType(e.target.value)}>
                {payTypes.map((p) => (
                  <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              fullWidth
              options={kktuOptions}
              value={kktu}
              onChange={(_, v) => setKktu(v)}
              onInputChange={(_, v) => setKktuSearch(v)}
              getOptionLabel={(o) => (o.code ? `${o.code} — ${o.name}` : '')}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              renderInput={(params) => (
                <TextField {...params} required label="ККТУ *" helperText="Поиск по словарю ОРД" />
              )}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField label="Бренд" value={brand} onChange={(e) => setBrand(e.target.value)} fullWidth />
            <TextField label="Категория" value={category} onChange={(e) => setCategory(e.target.value)} fullWidth />
            <TextField label="Target URL" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} fullWidth />
          </Stack>
          <TextField
            label="Описание"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Тексты (по строке)"
            value={texts}
            onChange={(e) => setTexts(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>
              Видео / медиафайлы для ОРД *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Лимит ОРД: до 260 Мб на файл. Можно выбрать несколько файлов сразу.
              При копировании старого креатива файлы нужно загрузить заново.
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,image/*,.mp4,.mov,.webm,.mkv,.avi,.jpg,.jpeg,.png,.gif,.webp"
              multiple
              hidden
              onChange={onPickFile}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={uploading || saving}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading
                  ? (uploadTotal > 1 ? `Загрузка ${uploadCurrent}/${uploadTotal}…` : 'Загрузка…')
                  : 'Выбрать файлы *'}
              </Button>
              {uploadedMedia.length === 0 && !mediaExternalIds.trim() && (
                <Typography variant="body2" color="text.secondary">Файлы ещё не выбраны</Typography>
              )}
            </Stack>
            {(uploadedMedia.length > 0 || mediaExternalIds.trim()) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                {uploadedMedia.map((m) => (
                  <Chip
                    key={m.mediaExternalId}
                    color="success"
                    label={`${m.filename} · ${formatSize(m.sizeBytes)} · ${m.mediaExternalId}`}
                    onDelete={uploading ? undefined : () => removeUploadedMedia(m.mediaExternalId)}
                  />
                ))}
                {uploadedMedia.length === 0 && mediaExternalIds.trim() && (
                  <Chip
                    color="default"
                    label={`ID: ${mediaExternalIds}`}
                    onDelete={() => setMediaExternalIds('')}
                  />
                )}
              </Stack>
            )}
            {uploading && (
              <Box sx={{ mt: 1.25 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption">
                  {uploadTotal > 1 ? `Файл ${uploadCurrent}/${uploadTotal}: ` : ''}
                  {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignSelf: { sm: 'flex-start' } }}>
            <Button
              variant="contained"
              disabled={saving || uploading || !canCreate}
              onClick={saveCreative}
              fullWidth={isMobile}
            >
              {saving
                ? <CircularProgress size={20} />
                : (editingId != null ? 'Сохранить' : 'Создать и отправить в ОРД')}
            </Button>
            <Button
              variant="outlined"
              disabled={saving || uploading}
              onClick={cancelForm}
              fullWidth={isMobile}
            >
              Отмена
            </Button>
          </Stack>
        </Stack>
        </AccordionDetails>
      </Accordion>

      {loading ? <CircularProgress /> : (
        <Box ref={listRef}>
          <TextField
            size="small"
            fullWidth
            label="Поиск по erid / названию"
            placeholder="например 2Vtzq…"
            value={eridSearch}
            onChange={(e) => setEridSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            helperText={eridSearch.trim() ? `Найдено: ${sortedItems.length}` : undefined}
          />
          {isMobile ? (
        <Stack spacing={1.25}>
          {sortedItems.map((c) => (
            <Box
              key={c.creativeDId}
              sx={highlightId === c.creativeDId ? { outline: '2px solid', outlineColor: 'success.main', borderRadius: 3 } : undefined}
            >
            <MobileEntityCard
              title={c.name}
              subtitle={
                c.ordCreatedTs
                  ? `ОРД: ${formatAppDateTime(c.ordCreatedTs)}`
                  : 'Дата ОРД неизвестна'
              }
              chips={
                <>
                  <Chip
                    size="small"
                    label={statusLabel[c.status] || c.status}
                    color={
                      c.status === 'REPORTING' ? 'success'
                        : c.status === 'MISSING_LINKS' ? 'error'
                          : c.status === 'PUBLICATION_ENDED' || c.status === 'SKIP_STATS' ? 'default'
                            : 'warning'
                    }
                    variant={c.status === 'SKIP_STATS' ? 'outlined' : 'filled'}
                  />
                  <Chip size="small" variant="outlined" label={c.creativeType === 'SELF_AD' ? 'сам' : 'сторон'} />
                </>
              }
              meta={[
                { label: 'ERID', value: c.erid || '—' },
                { label: 'ККТУ', value: c.kktu || '—' },
                { label: 'Цена', value: c.priceAmount ?? '—' },
                {
                  label: 'Ссылки',
                  value: [
                    c.youtubeVideoId ? `YT ${c.youtubeVideoId}` : null,
                    c.vkVideoId ? `VK ${c.vkVideoId}` : null,
                    c.rutubeVideoId ? `RT ${c.rutubeVideoId}` : null,
                    dzenEnabled && c.dzenVideoId ? `DZ ${c.dzenVideoId}` : null,
                  ].filter(Boolean).join(' · ') || '—',
                },
              ]}
              actions={renderCreativeActions(c)}
            />
            </Box>
          ))}
          {sortedItems.length === 0 && (
            <Typography color="text.secondary">Ничего не найдено</Typography>
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Создан в ОРД</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>ERID</TableCell>
                <TableCell>ККТУ</TableCell>
                <TableCell>Цена</TableCell>
                <TableCell>Ссылки</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((c) => (
                <TableRow
                  key={c.creativeDId}
                  sx={{
                    ...(c.status === 'SKIP_STATS' ? { opacity: 0.72 } : {}),
                    ...(highlightId === c.creativeDId ? { bgcolor: 'success.light', outline: '2px solid', outlineColor: 'success.main' } : {}),
                  }}
                >
                  <TableCell>
                    {c.ordCreatedTs ? formatAppDateTime(c.ordCreatedTs) : '—'}
                  </TableCell>
                  <TableCell>
                    {c.name}
                    {c.lastError && (
                      <Typography variant="caption" color="error" display="block">{c.lastError}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{c.creativeType === 'SELF_AD' ? 'сам' : 'сторон'}</TableCell>
                  <TableCell>{statusLabel[c.status] || c.status}</TableCell>
                  <TableCell><code>{c.erid || '—'}</code></TableCell>
                  <TableCell>{c.kktu || '—'}</TableCell>
                  <TableCell>{c.priceAmount ?? '—'}</TableCell>
                  <TableCell>
                    {c.youtubeVideoId && <div>YT: {c.youtubeVideoId}</div>}
                    {c.vkVideoId && <div>VK: {c.vkVideoId}</div>}
                    {c.rutubeVideoId && <div>RT: {c.rutubeVideoId}</div>}
                    {dzenEnabled && c.dzenVideoId && <div>DZ: {c.dzenVideoId}</div>}
                    {!c.youtubeVideoId && !c.vkVideoId && !c.rutubeVideoId && !(dzenEnabled && c.dzenVideoId) && '—'}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {renderCreativeActions(c)}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
        </Box>
      )}

      <Dialog
        open={linksOpen}
        onClose={() => setLinksOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>Ссылки для {linksCreative?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2">ERID: <code>{linksCreative?.erid}</code></Typography>
            <Typography variant="caption" color="text.secondary">
              Если ссылка уже привязана — она подставится в поле. Можно заменить и сохранить.
            </Typography>
            <TextField
              key={`yt-${linksCreative?.creativeDId || 'new'}`}
              label="YouTube URL"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              fullWidth
            />
            {platformsByHosting.YOUTUBE.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Площадка YouTube</InputLabel>
                <Select
                  label="Площадка YouTube"
                  value={youtubePlatformDId === '' ? '' : String(youtubePlatformDId)}
                  onChange={(e) => {
                    const raw = String(e.target.value);
                    setYoutubePlatformDId(raw ? Number(raw) : '');
                  }}
                >
                  <MenuItem value="">Авто</MenuItem>
                  {platformsByHosting.YOUTUBE.map((p) => (
                    <MenuItem key={p.platformDId} value={String(p.platformDId)}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              key={`vk-${linksCreative?.creativeDId || 'new'}`}
              label="VK Video URL"
              value={vkUrl}
              onChange={(e) => setVkUrl(e.target.value)}
              fullWidth
            />
            {platformsByHosting.VK_VIDEO.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Площадка VK</InputLabel>
                <Select
                  label="Площадка VK"
                  value={vkPlatformDId === '' ? '' : String(vkPlatformDId)}
                  onChange={(e) => {
                    const raw = String(e.target.value);
                    setVkPlatformDId(raw ? Number(raw) : '');
                  }}
                >
                  <MenuItem value="">Авто</MenuItem>
                  {platformsByHosting.VK_VIDEO.map((p) => (
                    <MenuItem key={p.platformDId} value={String(p.platformDId)}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              key={`rt-${linksCreative?.creativeDId || 'new'}`}
              label="Rutube URL"
              value={rutubeUrl}
              onChange={(e) => setRutubeUrl(e.target.value)}
              fullWidth
              placeholder="https://rutube.ru/video/…"
            />
            {platformsByHosting.RUTUBE.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Площадка Rutube</InputLabel>
                <Select
                  label="Площадка Rutube"
                  value={rutubePlatformDId === '' ? '' : String(rutubePlatformDId)}
                  onChange={(e) => {
                    const raw = String(e.target.value);
                    setRutubePlatformDId(raw ? Number(raw) : '');
                  }}
                >
                  <MenuItem value="">Авто</MenuItem>
                  {platformsByHosting.RUTUBE.map((p) => (
                    <MenuItem key={p.platformDId} value={String(p.platformDId)}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {dzenEnabled && (
              <TextField
                key={`dz-${linksCreative?.creativeDId || 'new'}`}
                label="Дзен URL"
                value={dzenUrl}
                onChange={(e) => setDzenUrl(e.target.value)}
                fullWidth
                placeholder="https://dzen.ru/video/watch/…"
                helperText="Полуавтомат: статистику сдаёте загрузкой отчёта на странице «Дзен»"
              />
            )}
            {dzenEnabled && platformsByHosting.DZEN.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Площадка Дзен</InputLabel>
                <Select
                  label="Площадка Дзен"
                  value={dzenPlatformDId === '' ? '' : String(dzenPlatformDId)}
                  onChange={(e) => {
                    const raw = String(e.target.value);
                    setDzenPlatformDId(raw ? Number(raw) : '');
                  }}
                >
                  <MenuItem value="">Авто</MenuItem>
                  {platformsByHosting.DZEN.map((p) => (
                    <MenuItem key={p.platformDId} value={String(p.platformDId)}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              key={`tg-${linksCreative?.creativeDId || 'new'}`}
              label="Telegram URL"
              value={telegramUrl}
              onChange={(e) => setTelegramUrl(e.target.value)}
              fullWidth
              placeholder="https://t.me/channel/123"
              helperText="Только публичные каналы (t.me/channel/msgId или t.me/s/channel/msgId)"
            />
            {platformsByHosting.TELEGRAM.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Площадка Telegram</InputLabel>
                <Select
                  label="Площадка Telegram"
                  value={telegramPlatformDId === '' ? '' : String(telegramPlatformDId)}
                  onChange={(e) => {
                    const raw = String(e.target.value);
                    setTelegramPlatformDId(raw ? Number(raw) : '');
                  }}
                >
                  <MenuItem value="">Авто</MenuItem>
                  {platformsByHosting.TELEGRAM.map((p) => (
                    <MenuItem key={p.platformDId} value={String(p.platformDId)}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!platforms.length && (
              <Alert severity="info">
                Создайте площадки в разделе «Площадки» со ссылкой на канал (не просто youtube.com) —
                система сама сопоставит ролик с нужной площадкой по каналу автора.
              </Alert>
            )}
            {!!platforms.length && (
              <Alert severity="info">
                Площадка подставится автоматически по каналу ролика. Выбор ниже — только если каналов
                одного хостинга несколько и автомат не уверен.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setLinksOpen(false)}>Отмена</Button>
          <Button variant="contained" disabled={saving} onClick={saveLinks}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreativesPage;
